import { motion } from "framer-motion";

import { content } from "../../content/content";
import { featuredStories, studentVoices } from "../../data/stories";
import {
  fadeUp,
  slideInLeft,
  slideInRight,
  staggerContainer,
} from "../../hooks/useScrollReveal";
import { FlourishDivider } from "../Flourish";

const EASE = [0.22, 1, 0.36, 1] as const;

type QuoteLineWeight = "normal" | "heavy" | "climax";

const PACE = 1.0;
const INITIAL_DELAY = 0.4;

const lineVariants = {
  hidden: { opacity: 0, y: 6 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      delay: INITIAL_DELAY + i * PACE,
      ease: EASE,
    },
  }),
};

function weightClasses(weight: QuoteLineWeight): string {
  switch (weight) {
    case "heavy":
      return "text-ink";
    case "climax":
      return "text-saffron-dark";
    default:
      return "text-ink-light";
  }
}

function PoojaFeature({ story }: { story: (typeof featuredStories)[number] }) {
  return (
    <motion.div
      className="grid items-start gap-12 lg:grid-cols-[5fr_6fr] lg:gap-20"
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      <motion.div
        className="overflow-hidden rounded-2xl"
        variants={slideInLeft}
      >
        {story.image && (
          <img
            data-edit-id={`data.featuredStories[${story.id}].image`}
            src={story.image}
            alt={`${story.name}, ${story.course} student at Sadafal Education`}
            className="aspect-[4/5] h-full w-full object-cover"
            loading="lazy"
          />
        )}
      </motion.div>

      <motion.div className="lg:pt-6" variants={slideInRight}>
        <p
          data-edit-id="content.stories.poojaEyebrow"
          className="mb-5 font-body text-xs font-medium tracking-[0.22em] text-saffron-dark uppercase"
        >
          {content.stories.poojaEyebrow}
        </p>

        <blockquote className="font-display text-[clamp(1.4rem,2.4vw,2.1rem)] leading-[1.45] font-light italic">
          {content.stories.poojaLines.map((line, i) => (
            <motion.span
              key={line.id}
              data-edit-id={`content.stories.poojaLines[${line.id}].text`}
              className={`block ${line.pause ? "mb-[0.55em]" : ""} ${weightClasses(line.weight)}`}
              variants={lineVariants}
              custom={i}
            >
              {line.text}
            </motion.span>
          ))}
        </blockquote>

        <motion.div className="mt-10 flex items-center gap-3" variants={fadeUp}>
          <span className="block h-px w-10 bg-saffron-dark" />
          <p className="font-body text-sm">
            <span
              data-edit-id={`data.featuredStories[${story.id}].name`}
              className="font-medium text-ink"
            >
              {story.name}
            </span>
            <span
              data-edit-id="content.stories.poojaCaption"
              className="text-ink-muted"
            >
              {content.stories.poojaCaption}
            </span>
          </p>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

function VoiceTile({ story }: { story: (typeof studentVoices)[number] }) {
  return (
    <motion.article
      data-edit-id={`data.studentVoices[${story.id}]`}
      className="mb-5 inline-block w-full rounded-2xl bg-warm-white/70 p-7 ring-1 ring-ink/5 lg:mb-6 lg:p-8"
      variants={fadeUp}
      style={{ breakInside: "avoid" }}
    >
      <blockquote
        data-edit-id={`data.studentVoices[${story.id}].quote`}
        className="font-display text-[clamp(1.05rem,1.4vw,1.35rem)] leading-[1.4] text-ink italic"
      >
        &ldquo;{story.quote}&rdquo;
      </blockquote>
      <p
        data-edit-id={`data.studentVoices[${story.id}].story`}
        className="mt-4 font-body text-sm leading-relaxed text-ink-muted"
      >
        {story.story}
      </p>
      <div className="mt-6 flex items-center gap-3">
        <span className="block h-px w-6 bg-saffron-dark" />
        <p className="font-body text-xs">
          <span
            data-edit-id={`data.studentVoices[${story.id}].name`}
            className="font-medium text-ink"
          >
            {story.name}
          </span>
          <span className="text-ink-muted">
            ,{" "}
            <span data-edit-id={`data.studentVoices[${story.id}].age`}>
              {story.age}
            </span>{" "}
            ·{" "}
            <span data-edit-id={`data.studentVoices[${story.id}].course`}>
              {story.course}
            </span>
          </span>
        </p>
      </div>
    </motion.article>
  );
}

export function Stories() {
  const pooja = featuredStories[0];

  return (
    <section
      id="stories"
      className="paper-grain relative bg-warm-cream/60 py-28 md:py-40"
    >
      <div
        className="absolute top-0 right-0 left-0 selvedge"
        aria-hidden="true"
      />

      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          className="mb-20 max-w-2xl"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <p
            data-edit-id="content.stories.eyebrow"
            className="mb-4 font-body text-xs font-medium tracking-[0.22em] text-saffron-dark uppercase"
          >
            {content.stories.eyebrow}
          </p>
          <h2
            data-edit-id="content.stories.headline"
            className="text-balance font-display text-[clamp(2rem,4vw,3.5rem)] text-ink"
          >
            {content.stories.headline}
          </h2>
          <p
            data-edit-id="content.stories.intro"
            className="mt-4 font-body text-lg text-ink-muted"
          >
            {content.stories.intro}
          </p>
        </motion.div>

        {pooja && <PoojaFeature story={pooja} />}

        <motion.div
          className="my-24 md:my-32"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.0, ease: EASE }}
        >
          <FlourishDivider />
        </motion.div>

        <motion.div
          className="mb-12 max-w-xl"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <p
            data-edit-id="content.stories.moreEyebrow"
            className="mb-3 font-body text-xs font-medium tracking-[0.22em] text-violet uppercase"
          >
            {content.stories.moreEyebrow}
          </p>
          <h3
            data-edit-id="content.stories.moreHeadline"
            className="text-balance font-display text-[clamp(1.5rem,2.6vw,2.2rem)] text-ink"
          >
            {content.stories.moreHeadline}
          </h3>
        </motion.div>

        <motion.div
          className="columns-1 gap-5 md:columns-2 md:gap-6 lg:columns-3"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {studentVoices.map((story) => (
            <VoiceTile key={story.id} story={story} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
